/** Typed errors raised by the storage layer; the UI maps them to user-facing messages. */

export class StorageError extends Error {
  constructor(message: string) {
    super(message);
    // Prefixed on purpose: Dexie rewrites errors whose `name` equals an IndexedDB error name
    // (such as "NotFoundError") into its own error classes, which would break `instanceof`.
    this.name = `WizTime${new.target.name}`;
  }
}

/** Input or a resulting record failed validation. */
export class ValidationError extends StorageError {
  readonly issues: readonly string[];
  constructor(message: string, issues: readonly string[] = []) {
    super(issues.length > 0 ? `${message}: ${issues.join('; ')}` : message);
    this.issues = issues;
  }
}

/** A referenced record does not exist or is deleted. */
export class NotFoundError extends StorageError {
  readonly recordId: string;
  constructor(recordId: string, what = 'record') {
    super(`The ${what} ${recordId} does not exist`);
    this.recordId = recordId;
  }
}

/** A project cannot be deleted because blocks still use it. */
export class ProjectInUseError extends StorageError {
  readonly projectId: string;
  readonly blockCount: number;
  constructor(projectId: string, blockCount: number) {
    super(`The project ${projectId} is used by ${blockCount} block(s)`);
    this.projectId = projectId;
    this.blockCount = blockCount;
  }
}

/** Why a project cannot be given the requested parent (data dictionary §3, project hierarchy). */
export const ProjectHierarchyReason = {
  /** The parent does not exist or is deleted. */
  ParentMissing: 'parentMissing',
  /** The project would be its own parent. */
  SelfParent: 'selfParent',
  /** The parent is a child project itself: only two levels are allowed. */
  ParentIsChild: 'parentIsChild',
  /** The project has child projects of its own and cannot become a child. */
  HasChildren: 'hasChildren',
} as const;
export type ProjectHierarchyReason = (typeof ProjectHierarchyReason)[keyof typeof ProjectHierarchyReason];

/** A project cannot be moved under the requested parent; nothing was written. */
export class ProjectHierarchyError extends StorageError {
  readonly projectId: string | null;
  readonly parentId: string;
  readonly reason: ProjectHierarchyReason;
  constructor(projectId: string | null, parentId: string, reason: ProjectHierarchyReason) {
    super(`The project ${projectId ?? '(new)'} cannot get the parent ${parentId}: ${reason}`);
    this.projectId = projectId;
    this.parentId = parentId;
    this.reason = reason;
  }
}

/** A parent project cannot be deleted while it has child projects; delete or move them first. */
export class ProjectHasChildrenError extends StorageError {
  readonly projectId: string;
  readonly childCount: number;
  constructor(projectId: string, childCount: number) {
    super(`The project ${projectId} has ${childCount} child project(s)`);
    this.projectId = projectId;
    this.childCount = childCount;
  }
}

/** A check-in item cannot be deleted because it has completed logs (history); deactivate it instead. */
export class CheckinInUseError extends StorageError {
  readonly itemId: string;
  readonly doneCount: number;
  constructor(itemId: string, doneCount: number) {
    super(`The check-in item ${itemId} has ${doneCount} completed log(s)`);
    this.itemId = itemId;
    this.doneCount = doneCount;
  }
}

export const UndoConflictReason = {
  /** A record of the step was changed after the step (other device, tab or import). */
  RemoteChange: 'remoteChange',
} as const;
export type UndoConflictReason = (typeof UndoConflictReason)[keyof typeof UndoConflictReason];

/** Undo or redo was aborted; the step has been dropped from the history. */
export class UndoConflictError extends StorageError {
  readonly reason: UndoConflictReason;
  readonly label: string;
  readonly recordIds: readonly string[];
  constructor(reason: UndoConflictReason, label: string, recordIds: readonly string[]) {
    super(`Cannot undo or redo "${label}": ${reason}`);
    this.reason = reason;
    this.label = label;
    this.recordIds = recordIds;
  }
}
